import { useRouter } from 'next/router';

import { Box, Button, ButtonGroup, Heading, Modal, Text, TextButton, Toast, useToast } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import {
  useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_delete,
  useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_delete,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { useGetListingDetails } from '../../../hooks/queries/properties';

import { emitConfirmDeleteFeeEvent, EVENT_IDS } from '../../../analytics/events/multiFamilyCostsAndFees';
import { useAuth } from '../../AuthProvider';
import type { DatarouterError } from '@zg-rentals/rental-manager-env';

type Props = {
  feeId: string;
  setShowDeleteFeeModal: (show: boolean) => void;
  isSingleFamilyFee: boolean;
};

const CostsAndFeesDeleteFeeModal = ({ feeId, setShowDeleteFeeModal, isSingleFamilyFee }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { user } = useAuth();

  const { enqueueToast } = useToast();

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const { mutate: onDeleteMF, isPending: isDeleteMFLoading } =
    useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_delete({
      onSuccess: () => {
        enqueueToast(<Toast tone="success">Fee deleted</Toast>);

        setShowDeleteFeeModal(false);
      },
      onError: (e) => {
        logError({
          error: e,
          context: {
            message: `Failed to delete multifamily fee for listingAlias: ${listingAlias}, feeGroupId: ${feeId}`,
            details: 'Delete multifamily fee',
          },
        });
        enqueueToast(
          <Toast tone="critical">
            {(e as DatarouterError).message || 'The fee couldn’t be deleted. Refresh the page and try again.'}
          </Toast>,
        );
      },
      meta: {
        invalidates: ['listingRecommendations', 'mf-costsAndFees'],
        context: { api: 'rental-manager', details: 'delete multifamily fee' },
      },
    });

  const { mutate: onDeleteSF, isPending: isDeleteSFLoading } =
    useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_delete({
      onSuccess: () => {
        enqueueToast(<Toast tone="success">Fee deleted</Toast>);

        setShowDeleteFeeModal(false);
      },
      onError: (e) => {
        logError({
          error: e,
          context: {
            message: `Failed to delete fee for listingAlias: ${listingAlias}, costAndFeeId: ${feeId}`,
            details: 'Delete single-family fee',
          },
        });
        enqueueToast(
          <Toast tone="critical">
            {(e as DatarouterError).message || 'The fee couldn’t be deleted. Refresh the page and try again.'}
          </Toast>,
        );
      },
      meta: {
        invalidates: ['listingRecommendations', 'sf-costsAndFees'],
        context: { api: 'rental-manager', details: 'delete single-family fee' },
      },
    });

  const handleDeleteClick = () => {
    if (isSingleFamilyFee) {
      onDeleteSF({
        getParams: {
          listingAlias,
          costAndFeeId: feeId,
        },
      });
    } else {
      onDeleteMF({
        getParams: {
          listingAlias,
          feeGroupId: feeId,
        },
      });
      if (listingDetails && user) {
        emitConfirmDeleteFeeEvent({ currentProperty: listingDetails, user });
      }
    }
  };

  const handleClose = () => {
    setShowDeleteFeeModal(false);
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && handleClose()}
      header={<Heading level={2}>Delete this fee?</Heading>}
      body={
        <div data-testid="CostsAndFeesDeleteFeeModal">
          <Box css={{ display: 'flex', justifyContent: 'center' }}>
            <Text>This will remove the fee from your listing.</Text>
          </Box>
        </div>
      }
      footer={
        <ButtonGroup aria-label="delete fee actions" size="sm">
          <TextButton disabled={isDeleteMFLoading || isDeleteSFLoading} aria-label="cancel" onClick={handleClose}>
            Cancel
          </TextButton>
          <Button
            disabled={isDeleteMFLoading || isDeleteSFLoading}
            aria-label="delete"
            tone="critical"
            emphasis="filled"
            onClick={handleDeleteClick}
            data-eventid={EVENT_IDS.CONFIRM_DELETE_FEE}
          >
            Delete
          </Button>
        </ButtonGroup>
      }
      size="xs"
    />
  );
};

export default CostsAndFeesDeleteFeeModal;
